import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  Divider,
  Theme,
} from "@mui/material";
import { motion } from "framer-motion";
import React from "react";
import ContentSection from "../ContentSection";
import { useTranslation } from "react-i18next";

interface HostContentProps {
  theme: Theme;
  getBannerImage: () => string;
}

const HostContent: React.FC<HostContentProps> = ({ theme, getBannerImage }) => {
  const { t } = useTranslation();

  return (
    <Container
      maxWidth={false}
      sx={{
        py: 4,
        px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
        maxWidth: "100%",
        mx: "auto",
      }}
    >
      {/* Main card */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "white",
          maxWidth: "100%",
          mx: "auto",
        }}
      >
        {/* Banner image */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 250, sm: 350, md: 400, lg: 450, xl: 500 },
          }}
        >
          <img
            src={getBannerImage()}
            alt="Host Portal"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
              p: { xs: 3, sm: 4, md: 5 },
            }}
          >
            <Typography
              variant="h3"
              fontWeight={700}
              color="white"
              gutterBottom
              sx={{ fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" } }}
            >
              Welcome to CYPMS
            </Typography>
            <Typography
              variant="h5"
              fontWeight={500}
              color="white"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" } }}
            >
              Empowering the Future Workforce
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <HostIntroSection />
          <Divider sx={{ my: 4 }} />
          <HostFeaturesSection theme={theme} />
          <Divider sx={{ my: 4 }} />
          <HostResourcesSection theme={theme} />
        </Box>
      </Card>
    </Container>
  );
};

// Host intro section
const HostIntroSection: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.7 }}
    >
      <Typography
        variant="h4"
        fontWeight={700}
        gutterBottom
        color="primary"
        sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" } }}
      >
        What Is CYPMS?
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }} color="text.secondary">
        The CODICE Youth Program Management System (CYPMS) is a secure and
        intuitive platform built to help Host Organizations like yours actively
        engage in DC's youth employment programs—such as MBSYEP and others.
      </Typography>
      <Typography variant="body1" sx={{ mb: 4 }} color="text.secondary">
        From enrolling your organization to managing worksites, posting job
        openings, supervising youth, and approving hours—CYPMS brings it all
        together in one place.
      </Typography>
    </motion.div>
  );
};

interface SectionWithThemeProps {
  theme: Theme;
}

// Host features section
const HostFeaturesSection: React.FC<SectionWithThemeProps> = ({ theme }) => {
  return (
    <>
      <Typography
        variant="h4"
        fontWeight={700}
        gutterBottom
        color="primary"
        sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" } }}
      >
        Things You Should Know About CYPMS
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }} color="text.secondary">
        Here's what you can do once your organization is registered:
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Enroll Your Organization in a Program"
            content="Sign up to participate in youth employment programs and manage your involvement each year."
            delay={0.1}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Set Up Worksites and Teams"
            content="Create and manage worksites, add supervisors, and define roles and capacities for each location."
            delay={0.2}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Post Job Openings"
            content="Publish internship or employment opportunities for youth. You can even use our AI-powered assistant to generate job advertisements based on your prompt."
            delay={0.3}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="View and Manage Job Applications"
            content="See who's applied for your posted jobs and make informed selections based on application details."
            delay={0.4}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Assign and Supervise Youth"
            content="Supervisors can oversee youth performance, log feedback, and support their learning experience."
            delay={0.5}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Approve Timesheets"
            content="Review and approve youth-submitted timesheets directly through the portal—ensuring accurate and timely payroll processing."
            delay={0.6}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Submit Program Forms"
            content="Report incidents, request transfers, or submit terminations through streamlined online forms."
            delay={0.7}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Receive Real-Time Notifications"
            content="Stay updated with system messages, alerts, and important reminders from DOES."
            delay={0.8}
          />
        </Grid>
      </Grid>
    </>
  );
};

// Host resources section
const HostResourcesSection: React.FC<SectionWithThemeProps> = ({ theme }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.9 }}
    >
      <Card
        elevation={1}
        sx={{
          p: 4,
          borderRadius: 2,
          bgcolor: theme.palette.grey[50],
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
          What Happens After Registration?
        </Typography>
        <Box component="ul" sx={{ pl: 4, mb: 0 }}>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Complete Your Organization Profile – Add your team, roles, and
            contact details
          </Box>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Enroll in a Program – Confirm your interest in MBSYEP or similar
            initiatives
          </Box>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Create Worksites & Assign Supervisors – Organize how youth will be
            hosted
          </Box>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Publish Job Openings – List available roles and review applications
          </Box>
          <Box component="li" sx={{ mb: 0, color: "text.secondary" }}>
            Approve Timesheets and Monitor Youth – Manage youth participation
            and engagement
          </Box>
        </Box>
      </Card>
    </motion.div>
  );
};

export default HostContent;
